import { useContext, useEffect, useState } from 'react';
import { UpdateCauseType, MatrixType } from '../../../../types/types';
import { useDispatch, useSelector } from 'react-redux';
import { getAllMatricesByProjectId } from '../../../../features/projects/matrixSlices';

import {
  deleteAction,
  deleteCause,
  deleteConsequence,
  deleteSafeguard,
  getDeviation,
  getProjectActionsByProjectId,
  updateAction,
  UpdateActionType,
  updateCause,
  updateConsequence,
  UpdateConsequenceType,
  updateSafeguard,
  UpdateSafeguardType,
} from '../../../../features/projects/deviationSlice';
import { Col, Row } from 'antd';
import { NotificationContext } from '../../../../providers/NotificationProvider';
import DeviationTable from './DeviationTable';

type DeviationProps = {
  id: number;
  nodeOrder?: number;
  deviationOrder?: number;
};

const Deviation: React.FC<DeviationProps> = ({ id, nodeOrder, deviationOrder }) => {
  //const params = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const deviation = useSelector((store: any) => store.deviation.deviation);
  const { triggerNotification } = useContext(NotificationContext);

  type EditFormStatusType = {
    causes: boolean;
    consequences: boolean;
    safeguards: boolean;
    actions: boolean;
  };

  const [editedForm, setEditedForm] = useState<EditFormStatusType>({
    causes: false,
    consequences: false,
    safeguards: false,
    actions: false,
  });

  type EditFormValuesType = {
    cause: {
      id?: number;
      name: string;
    };
    consequence: {
      id?: number;
      name: string;
    };
    safeguard: {
      id?: number;
      name: string;
    };
    action: {
      id?: number;
      name: string;
      responsability: string;
      asociated_risk: string;
      consequences: number[];
      project_order?: number | null;
    };
  };

  const [editedFormValues, setEditedFormValues] = useState<EditFormValuesType>({
    cause: {
      name: '',
    },
    consequence: {
      name: '',
    },
    safeguard: {
      name: '',
    },
    action: {
      name: '',
      responsability: '', // <-- corregido aquí
      asociated_risk: '',
      consequences: [],
      project_order: null,
    },
  });

  // Nuevo estado para saber qué elemento se está editando (por id y tipo)
  const [editingItem, setEditingItem] = useState<{ type: string; id: number | null }>({ type: '', id: null });

  const normalizeActionId = (value: unknown): number | null => {
    if (typeof value === 'number' && !Number.isNaN(value) && value > 0) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = Number(trimmed);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return null;
  };

  // Estado para guardar matrices asociadas al proyecto
  const [matrices, setMatrices] = useState<MatrixType[]>([]);

  useEffect(() => {
    if (!id) {
      return;
    }

    getCurrentDeviation();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!deviation.project) {
      return;
    }

    getMatricesAvailable();
    // eslint-disable-next-line
  }, [deviation.project]);

  const getMatricesAvailable = async () => {
    if (!deviation.project) {
      console.error('No hay proyecto asociado a la desviación');
      return;
    }

    dispatch(getAllMatricesByProjectId(deviation.project) as any)
      .then((result: any) => {
        if (getAllMatricesByProjectId.fulfilled.match(result)) {
          setMatrices(result.payload);
        } else {
          triggerNotification('error', 'Error', 'Error al cargar las matrices', 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', 'Error', 'Error al cargar las matrices', 'bottomRight');
      });
  };

  const getCurrentDeviation = async () => {
    if (!id) return;
    dispatch(getDeviation(id) as any).catch((error: any) => {
      console.error('Error:', error);
    });
  };

  const modifyCause = () => {
    if (!editedFormValues.cause.name || editedFormValues.cause.name === '') {
      triggerNotification('error', 'Error', 'El nombre de la causa no puede estar vacío', 'bottomRight');
      return;
    }

    if (!editedFormValues.cause.id) {
      return;
    }

    const updatedCause: UpdateCauseType = {
      id: editedFormValues.cause.id ?? 0,
      name: editedFormValues.cause.name,
    };

    dispatch(updateCause(updatedCause) as any)
      .then((result: any) => {
        if (updateCause.fulfilled.match(result)) {
          triggerNotification(
            'success',
            'Causa actualizada',
            'La causa ha sido actualizada correctamente',
            'bottomRight'
          );
          setEditedForm({ ...editedForm, causes: false });
          setEditedFormValues({ ...editedFormValues, cause: { name: '' } });
          setEditingItem({ type: '', id: null }); // Limpiar edición
          if (deviation?.id !== null && deviation?.id !== undefined) {
            void dispatch(getDeviation(deviation.id) as any);
          }
        }
      })
      .catch((error: any) => {
        triggerNotification('error', 'Error', error.error, 'bottomRight');
      });
  };

  const modifyConsequence = () => {
    if (!editedFormValues.consequence.name || editedFormValues.consequence.name === '') {
      triggerNotification('error', 'Error', 'El nombre de la consecuencia no puede estar vacío', 'bottomRight');
      return;
    }

    if (!editedFormValues.consequence.id) {
      return;
    }

    const updatedConsequence: UpdateConsequenceType = {
      id: editedFormValues.consequence.id ?? 0,
      name: editedFormValues.consequence.name,
    };

    dispatch(updateConsequence(updatedConsequence) as any)
      .then((result: any) => {
        if (updateConsequence.fulfilled.match(result)) {
          triggerNotification(
            'success',
            'Consecuencia actualizada',
            'La consecuencia ha sido actualizada correctamente',
            'bottomRight'
          );
          setEditedForm({ ...editedForm, consequences: false });
          setEditedFormValues({ ...editedFormValues, consequence: { name: '' } });
          setEditingItem({ type: '', id: null }); // Limpiar edición
          if (deviation?.id !== null && deviation?.id !== undefined) {
            void dispatch(getDeviation(deviation.id) as any);
          }
        }
      })
      .catch((error: any) => {
        triggerNotification('error', 'Error', error.error, 'bottomRight');
      });
  };

  const modifySafeguard = () => {
    if (!editedFormValues.safeguard.name || editedFormValues.safeguard.name === '') {
      triggerNotification('error', 'Error', 'El nombre de la salvaguarda no puede estar vacío', 'bottomRight');
      return;
    }

    if (!editedFormValues.safeguard.id) {
      return;
    }

    const updatedSafeguard: UpdateSafeguardType = {
      id: editedFormValues.safeguard.id ?? 0,
      name: editedFormValues.safeguard.name,
    };

    dispatch(updateSafeguard(updatedSafeguard) as any)
      .then((result: any) => {
        if (updateSafeguard.fulfilled.match(result)) {
          triggerNotification(
            'success',
            'Salvaguarda actualizada',
            'La salvaguarda ha sido actualizada correctamente',
            'bottomRight'
          );
          setEditedForm((prev: any) => ({ ...prev, safeguards: false }));
          setEditedFormValues((prev: any) => ({ ...prev, safeguard: { name: '' } }));
          setEditingItem({ type: '', id: null }); // Limpiar edición
          if (deviation?.id !== null && deviation?.id !== undefined) {
            void dispatch(getDeviation(deviation.id) as any);
          }
        }
      })
      .catch((error: any) => {
        triggerNotification('error', 'Error', error.error, 'bottomRight');
      });
  };

  const modifyAction = (actionId?: number | null) => {
    const name = (editedFormValues.action.name ?? '').trim();
    if (!name) {
      triggerNotification('error', 'Error', 'El nombre de la acción no puede estar vacío', 'bottomRight');
      return;
    }

    const rawId =
      actionId ??
      editedFormValues.action.id ??
      (editingItem.type === 'action' ? editingItem.id : null) ??
      null;
    const parsedId = normalizeActionId(rawId);

    if (!parsedId) {
      triggerNotification('error', 'Error', 'No se pudo identificar la acción a actualizar', 'bottomRight');
      return;
    }

    const updatedAction: UpdateActionType = {
      id: parsedId,
      name,
      responsability: editedFormValues.action.responsability ?? '',
      asociated_risk: editedFormValues.action.asociated_risk ?? '',
      project_order:
        editedFormValues.action.project_order ??
        ((deviation?.actions ?? []).find((item: any) => item?.id === parsedId)?.project_order ?? null),
      consequences: (() => {
        const fromEdited = Array.isArray(editedFormValues.action.consequences)
          ? editedFormValues.action.consequences
          : [];
        const fromState = (deviation?.actions ?? [])
          .find((item: any) => item?.id === parsedId)?.consequences ?? [];
        const combined = new Set<number>();
        fromEdited.forEach((value: any) => {
          if (typeof value === 'number' && !Number.isNaN(value)) {
            combined.add(value);
          }
        });
        (Array.isArray(fromState) ? fromState : []).forEach((value: any) => {
          if (typeof value === 'number' && !Number.isNaN(value)) {
            combined.add(value);
          }
        });
        return Array.from(combined.values());
      })(),
    };

    dispatch(updateAction(updatedAction) as any)
      .then((result: any) => {
        if (updateAction.fulfilled.match(result)) {
          triggerNotification(
            'success',
            'Acción actualizada',
            'La acción ha sido actualizada correctamente',
            'bottomRight'
          );
          setEditedForm({ ...editedForm, actions: false });
          setEditedFormValues({
            ...editedFormValues,
            action: {
              id: undefined,
              name: '',
              responsability: '',
              asociated_risk: '',
              consequences: [],
              project_order: null,
            },
          });
          setEditingItem({ type: '', id: null });
          if (deviation?.id) {
            void dispatch(getDeviation(deviation.id) as any);
          }
        } else if (result?.payload?.error) {
          triggerNotification('error', 'Error', result.payload.error, 'bottomRight');
        }
      })
      .catch((error: any) => {
        triggerNotification('error', 'Error', error?.error ?? 'Error al actualizar la acción', 'bottomRight');
      });
  };

  const confirmDelete = (type: string, item: any) => {
    if (
      type.length === 0 ||
      item === 0 ||
      (type === 'consequence' ? !item.id : type === 'safeguard' ? !item.id : type === 'action' ? !item.id : !item.key)
    )
      return;
    const itemId = type === 'consequence' || type === 'safeguard' || type === 'action' ? item.id : item.key;
    if (type === 'action') {
      const consequenceFromItem = item?.consequenceId ?? item?.consequence_id ?? null;
      const fallbackConsequence = Array.isArray(item?.consequences) ? item.consequences[0] ?? null : null;
      const consequenceId = consequenceFromItem ?? fallbackConsequence ?? null;
      dispatch(deleteAction({ actionId: itemId, consequenceId }) as any)
        .then((result: any) => {
          if (deleteAction.fulfilled.match(result)) {
            triggerNotification(
              'success',
              'Elemento eliminado',
              'La recomendación se eliminó correctamente',
              'bottomRight'
            );
            const deviationId = typeof deviation?.id === 'number' ? deviation.id : null;
            if (deviationId !== null) {
              void dispatch(getDeviation(deviationId) as any);
            }
            const projectId = typeof deviation?.project === 'number' ? deviation.project : null;
            if (projectId !== null) {
              void dispatch(getProjectActionsByProjectId(projectId) as any);
            }
          }
        })
        .catch((error: any) => {
          triggerNotification('error', 'Error', error?.error ?? 'No se pudo eliminar la recomendación', 'bottomRight');
        });
      return;
    }
    // Mapeo de tipos a thunks
    const thunkMap: Record<string, any> = {
      cause: deleteCause,
      consequence: deleteConsequence,
      safeguard: deleteSafeguard,
    };

    const thunk = thunkMap[type];

    if (!thunk) {
      triggerNotification('error', 'Error', 'Tipo de elemento no válido', 'bottomRight');
      return;
    }

    dispatch(thunk(itemId) as any)
      .then((result: any) => {
        if (thunk.fulfilled.match(result)) {
          triggerNotification(
            'success',
            'Elemento eliminado',
            'El elemento ha sido eliminado correctamente',
            'bottomRight'
          );
        }
      })
      .catch((error: any) => {
        triggerNotification('error', 'Error', error.error, 'bottomRight');
      });
  };

  return (
    <Row className='content'>
      {deviation && deviation.id !== null ? (
        <Col sm={24}>
          <h1>
            {nodeOrder !== undefined && nodeOrder !== null && deviationOrder !== undefined && deviationOrder !== null
              ? `${nodeOrder}.${deviationOrder} ${deviation.name}`
              : deviation.name}
          </h1>
          <Row className='deviation-section'>
            <DeviationTable
              deviation={deviation}
              onEdit={(type: any, record: any) => {
                // Aseguramos que el campo name esté correctamente seteado
                let valueToEdit = { ...record };
                if (type === 'cause') {
                  valueToEdit = {
                    id: record.id ?? record.causeId ?? record.key,
                    name: record.name ?? record.causeName ?? record.cause,
                  };
                } else if (type === 'consequence') {
                  valueToEdit = { id: record.id, name: record.name };
                } else if (type === 'safeguard') {
                  valueToEdit = { id: record.id, name: record.name };
                } else if (type === 'action') {
                  const rawConsequences = Array.isArray(record.consequences) ? record.consequences : [];
                  const normalizedConsequences = rawConsequences
                    .map((item: any) => {
                      if (typeof item === 'number') return item;
                      if (item && typeof item === 'object' && 'id' in item) {
                        return item.id ?? null;
                      }
                      return null;
                    })
                    .filter((id: any): id is number => typeof id === 'number' && !Number.isNaN(id));
                  const resolvedActionId =
                    normalizeActionId(record.id ?? record.actionId ?? record.action_id ?? record.key) ?? null;
                  valueToEdit = {
                    id: resolvedActionId,
                    name: record.name,
                    responsability: record.responsability || '', // corregido aquí
                    asociated_risk: record.asociated_risk || '',
                    consequences: normalizedConsequences,
                    project_order:
                      typeof record.project_order === 'number' && !Number.isNaN(record.project_order)
                        ? record.project_order
                        : null,
                  };
                }
                setEditedFormValues({
                  ...editedFormValues,
                  [type]: valueToEdit,
                });
                setEditedForm({ ...editedForm, [type + 's']: true });
                setEditingItem({
                  type,
                  id: valueToEdit.id ?? record.id ?? record.causeId ?? record.key ?? null,
                });
              }}
              onDelete={confirmDelete}
              onAdd={(type: any) => {
                setEditedFormValues({
                  ...editedFormValues,
                  [type]:
                    type === 'action'
                      ? { name: '', responsability: '', asociated_risk: '', consequences: [], project_order: null }
                      : { name: '' },
                });
                setEditedForm({ ...editedForm, [type + 's']: true });
                setEditingItem({ type, id: null });
              }}
              editingItem={editingItem}
              setEditingItem={setEditingItem}
              editedFormValues={editedFormValues}
              setEditedFormValues={setEditedFormValues}
              modifyCause={modifyCause}
              modifyConsequence={modifyConsequence}
              modifySafeguard={modifySafeguard}
              modifyAction={modifyAction}
              matricesAvailable={matrices} // Pasamos las matrices disponibles
            />
          </Row>
        </Col>
      ) : (
        <p>Cargando...</p>
      )}
    </Row>
  );
};

export default Deviation;
